import type { ReactNode } from 'react'

/**
 * The stears.co hero backdrop, reproduced from the homepage, where the markup is a
 * black ground carrying `/images/red_bg.webp` with the platform screenshot on top
 * of it under two black gradients.
 *
 * The order here differs from the site for one reason. Those gradients are tuned to
 * large white hero type; these pages carry 12px field labels and a password box, so
 * the screenshot needs to sit much further back. Pushing it back with a flat scrim
 * also flattened the red glow, because on the site the glow is underneath. The glow
 * is therefore painted last with a screen blend, which adds light where the artwork
 * is red and contributes nothing where it is black. The result keeps both the
 * screenshot texture and the glow while leaving the form fully readable.
 *
 * The screenshot is decorative, so it is hidden from assistive technology and takes
 * no pointer events.
 */

/** Pushes the screenshot back so foreground text stays legible. */
const VEIL =
  'linear-gradient(180deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 16%, rgba(0,0,0,0.55) 34%,' +
  ' rgba(0,0,0,0.55) 52%, rgba(0,0,0,0.5) 78%, rgba(0,0,0,0.82) 100%)'

/**
 * Darkens the middle, where the heading and form sit. The site uses
 * `ellipse 72% 78%`, which suits hero text centred in a narrow column. These pages
 * put content across the full width, so the ellipse is widened to keep the far left
 * and right from competing with the chart underneath. The stops are the site's.
 */
const VIGNETTE =
  'radial-gradient(ellipse 108% 86% at 50% 42%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.6) 50%,' +
  ' rgba(0,0,0,0.3) 72%, rgba(0,0,0,0) 96%)'

/** Height of the decorative band, matching the site's hero section. */
const BAND = 'clamp(640px, 92vh, 900px)'

interface StearsBackdropProps {
  children: ReactNode
  className?: string
}

export function StearsBackdrop({ children, className = '' }: StearsBackdropProps) {
  return (
    <div
      className={`relative h-full overflow-y-auto ${className}`}
      style={{ backgroundColor: '#000000' }}
    >
      <div
        aria-hidden
        data-testid="stears-backdrop"
        className="absolute inset-x-0 top-0 overflow-hidden"
        style={{ height: BAND, pointerEvents: 'none' }}
      >
        <img
          src="/images/stears-hero.webp"
          alt=""
          className="w-full h-full"
          style={{ objectFit: 'cover', objectPosition: 'top center' }}
        />
        <div className="absolute inset-0" style={{ background: VEIL }} />
        <div className="absolute inset-0" style={{ background: VIGNETTE }} />
        <div
          className="absolute inset-0"
          style={{
            // The glow is anchored slightly above the top edge and scaled past the
            // viewport, because the artwork has an inset rounded border baked into
            // its canvas that would otherwise show as faint lines down the sides.
            backgroundImage: "url('/images/red_bg.webp')",
            backgroundPosition: '50% -4%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '108% auto',
            mixBlendMode: 'screen',
          }}
        />
      </div>

      <div className="relative">{children}</div>
    </div>
  )
}
